import React, { FC, useState, useEffect, Fragment } from 'react';
import { Alert, Box, Tab, Button, Typography } from '@mui/material';
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import { LabClient } from 'models/client-directory';
import { useNavigate, useLocation, useMatch, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import AssociatedAction from './associated-action/associated-action.component';
import ConnectedClinicAction from '../connected-clinic-action/connected-clinic-action.component';
import { useMutation, useQuery } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { addPriceListToClientTemplate, addMaterialPriceListToClientTemplate } from 'apollo/templates/client-directory';
import { ActionPriceList } from 'models/action';
import { MaterialPriceList } from 'models/material';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { getMaterialPriceListsTemplate } from 'apollo/templates/material';
import {
    actionPriceListForClientTemplate,
    materialPriceListForClientTemplate,
} from 'apollo/templates/client-directory';
import Loader from 'components/shared/loader/loader.component';
import { getActionPriceListsTemplate } from 'apollo/templates/action';
import { downloadActionBasedClientActionPriceListPdf } from 'components/order-details/main-order-view/document-print-actions/files';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { buildPriceListPdfFileName } from 'utils/file';
import AssociatedMaterial from './associated-material/associated-material.component';

type PriceListProps = {
    labClient: LabClient;
};
const PriceList: FC<PriceListProps> = ({ labClient }: PriceListProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory']);
    const navigate = useNavigate();
    const location = useLocation();
    const [tabState, setTabState] = useState('#action-list');
    const match = useMatch('/client-directory/clients/:id/:tab');
    const { id: clientId } = useParams<{ id: string }>();
    const [changesSaved, setChangesSaved] = useState(false);
    const [clientOrderType, setClientOrderType] = useState('');
    const [error, setError] = useState<Error | null>(null);
    const [actionPriceList, setActionPriceList] = useState<ActionPriceList>({} as ActionPriceList);
    const [materialPriceList, setMaterialPriceList] = useState<MaterialPriceList>({} as MaterialPriceList);
    // The last-saved/merged lists from the server (with the client's overrides),
    // used to restore client prices when the user re-selects their current list
    // from the base lists in the picker.
    const [savedActionPriceList, setSavedActionPriceList] = useState<ActionPriceList>({} as ActionPriceList);
    const [savedMaterialPriceList, setSavedMaterialPriceList] = useState<MaterialPriceList>({} as MaterialPriceList);
    // Editable "use default" intent per list (applied on Save). hasUnsavedChanges
    // tracks any local edit (select / change / use-default / discount) so the page
    // can prompt the user to Save.
    const [actionUseDefault, setActionUseDefault] = useState(false);
    const [materialUseDefault, setMaterialUseDefault] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [spinner, setSpinner] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    useEffect(() => {
        setTabState(location.hash);
    }, [location]);

    useEffect(() => {
        if (labClient) {
            const orderType = customerSettings.getCustomerSetting(
                labClient.customer?.customerSettings || [],
                CONSTANTS.SETTINGS_NAMES.ORDER_VERSION,
            );
            if (orderType) {
                setClientOrderType(orderType);
            }
        }
    }, [labClient]);

    const { data: { materialPriceLists } = {}, loading: materialPriceListsLoading } = useQuery(
        getMaterialPriceListsTemplate,
        {
            notifyOnNetworkStatusChange: true,
            onError(error) {
                showBoundary(error);
            },
        },
    );

    const { loading: priceListLoading, refetch: refetchMaterialList } = useQuery(materialPriceListForClientTemplate, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true,
        variables: {
            clientId: clientId,
        },
        onCompleted: ({ materialPriceListForClient }) => {
            setMaterialPriceList(materialPriceListForClient || ({} as MaterialPriceList));
            setSavedMaterialPriceList(materialPriceListForClient || ({} as MaterialPriceList));
            setMaterialUseDefault(!!materialPriceListForClient?.isUsingDefault);
            setHasUnsavedChanges(false);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const { data: { actionPriceLists } = {}, loading: actionPriceListsLoading } = useQuery(
        getActionPriceListsTemplate,
        {
            notifyOnNetworkStatusChange: true,
            onError(error) {
                showBoundary(error);
            },
        },
    );

    const { loading: actionPriceListLoading, refetch: refetchActionList } = useQuery(actionPriceListForClientTemplate, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true,
        variables: {
            clientId: clientId,
        },
        onCompleted: ({ actionPriceListForClient }) => {
            setActionPriceList(actionPriceListForClient || ({} as ActionPriceList));
            setSavedActionPriceList(actionPriceListForClient || ({} as ActionPriceList));
            setActionUseDefault(!!actionPriceListForClient?.isUsingDefault);
            setHasUnsavedChanges(false);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleTabChange = (_: unknown, newTabState: string) => {
        if (tabState !== newTabState) {
            navigate(`${match?.pathnameBase}${newTabState}`);
            setTabState(newTabState);
        }
    };

    const [addPriceListToClient] = useMutation(addPriceListToClientTemplate);
    const [addMaterialPriceListToClient] = useMutation(addMaterialPriceListToClientTemplate);

    const buildActionListInput = (priceList: ActionPriceList) => ({
        _id: priceList?._id || '',
        actionPriceListName: priceList.actionPriceListName,
        currency: priceList.currency,
        generalDiscount: parseFloat(priceList.generalDiscount || '0'),
        actions: priceList.actions?.map((action) => ({
            _id: action._id,
            action: action.action,
            discountedPrice: action.discountedPrice,
            customerSpecificTiotandvardPrice: action.customerSpecificTiotandvardPrice,
        })),
    });

    const buildMaterialListInput = (priceList: MaterialPriceList) => ({
        _id: priceList?._id || '',
        materialPriceListName: priceList.materialPriceListName,
        currency: priceList.currency,
        generalDiscount: parseFloat(priceList.generalDiscount || '0'),
        materials: priceList.materials?.map((material) => ({
            _id: material._id,
            material: material.material,
            discountedPrice: material.discountedPrice,
        })),
    });

    // Selecting a list or toggling "use default" only updates local state; nothing
    // is persisted until Save. This keeps Save atomic so the action and material
    // lists can be migrated to a new (matching) currency in one operation, and lets
    // the backend validate the full intended state (effective currencies) at once.
    const onActionListChange = (actionList: ActionPriceList) => {
        setActionPriceList(actionList);
        setActionUseDefault(false);
        setChangesSaved(false);
        setHasUnsavedChanges(true);
    };

    const onMaterialListChange = (materialList: MaterialPriceList) => {
        setMaterialPriceList(materialList);
        setMaterialUseDefault(false);
        setChangesSaved(false);
        setHasUnsavedChanges(true);
    };

    const onUseDefaultAction = () => {
        setActionUseDefault(true);
        setChangesSaved(false);
        setHasUnsavedChanges(true);
    };

    const onUseDefaultMaterial = () => {
        setMaterialUseDefault(true);
        setChangesSaved(false);
        setHasUnsavedChanges(true);
    };

    // Simple-order clients save action and material together in one atomic call.
    // A list on "use default" is sent as null; the backend resolves the lab
    // defaults and validates the effective currencies (throws CURRENCY_MISMATCH).
    const saveSimplePriceLists = async () => {
        setError(null);
        try {
            await addPriceListToClient({
                variables: {
                    clientId,
                    actionList: actionUseDefault ? null : buildActionListInput(actionPriceList),
                    materialList: materialUseDefault ? null : buildMaterialListInput(materialPriceList),
                },
            });
            setChangesSaved(true);
            setHasUnsavedChanges(false);
            await Promise.all([refetchActionList(), refetchMaterialList()]);
        } catch (mutationError) {
            setError(mutationError as Error);
        }
    };

    // Action-based clients only manage the material list (action pricing comes
    // from the connected clinic). A null list falls back to the lab default; the
    // backend validates the effective material currency against the client currency.
    const saveMaterialPriceList = async () => {
        setError(null);
        try {
            await addMaterialPriceListToClient({
                variables: {
                    clientId,
                    materialList: materialUseDefault ? null : buildMaterialListInput(materialPriceList),
                },
            });
            setChangesSaved(true);
            setHasUnsavedChanges(false);
            await refetchMaterialList();
        } catch (mutationError) {
            setError(mutationError as Error);
        }
    };

    const handleActionBasedClientActionPriceListPdf = async () => {
        try {
            setError(null);
            setSpinner(true);
            // Same name the page header shows for this client - `labClient.name` is not
            // part of the labClient query, so it would always be undefined here.
            const clientName = labClient.clientContent?.name || labClient.customer?.name;
            await downloadActionBasedClientActionPriceListPdf(
                buildPriceListPdfFileName(currentOrgVar().name, clientName),
                labClient.customerId,
            );
        } catch (error) {
            setError(error as Error);
        } finally {
            setSpinner(false);
        }
    };

    if (materialPriceListsLoading || priceListLoading || actionPriceListLoading || actionPriceListsLoading) {
        return <Loader />;
    }

    return (
        <Fragment>
            <Typography variant="h2" mb={1}>
                {translate('client-directory:PRICE_LISTS')}
            </Typography>
            {changesSaved ? <AlertSnackbar type="success" message={translate('common:CHANGES_SAVED')} /> : undefined}
            {error ? (
                <AlertSnackbar
                    onClose={() => setError(null)}
                    type="error"
                    message={translate(`client-directory:${error.message}`)}
                />
            ) : undefined}
            {hasUnsavedChanges && (
                <Box mb={2}>
                    <Alert severity="info">{translate('client-directory:UNSAVED_PRICE_LIST_CHANGES')}</Alert>
                </Box>
            )}
            <TabContext value={tabState}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <TabList onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                        <Tab label={translate('client-directory:ACTION_LIST')} value="#action-list" />
                        <Tab label={translate('client-directory:MATERIAL_LIST')} value="#material-list" />
                    </TabList>
                    {!labClient.customer.parentClinicId &&
                        clientOrderType === 'Action-based' &&
                        tabState === '#action-list' && (
                            <LoadingButton
                                loading={spinner}
                                onClick={handleActionBasedClientActionPriceListPdf}
                                startIcon={<PictureAsPdfIcon />}
                            >
                                {translate('common:EXPORT_PDF')}
                            </LoadingButton>
                        )}
                    {!labClient.customer.parentClinicId && clientOrderType === 'Simple' && (
                        <Button variant="contained" onClick={saveSimplePriceLists}>
                            {translate('common:SAVE')}
                        </Button>
                    )}
                    {!labClient.customer.parentClinicId &&
                        clientOrderType === 'Action-based' &&
                        tabState === '#material-list' && (
                            <Button variant="contained" onClick={saveMaterialPriceList}>
                                {translate('client-directory:SAVE_MATERIAL_LIST')}
                            </Button>
                        )}
                </Box>

                <TabPanel sx={{ padding: 0 }} value="#action-list">
                    {clientOrderType === 'Simple' && (
                        <AssociatedAction
                            labClient={labClient}
                            actionPriceLists={actionPriceLists}
                            actionPriceListForClient={actionPriceList}
                            savedPriceList={savedActionPriceList}
                            isUsingDefault={actionUseDefault}
                            onActionListChange={onActionListChange}
                            onUseDefault={onUseDefaultAction}
                        />
                    )}
                    {clientOrderType === 'Action-based' && (
                        <ConnectedClinicAction
                            clientCurrency={labClient.actionBasedCurrency || ''}
                            labClient={labClient}
                        />
                    )}
                </TabPanel>
                <TabPanel sx={{ padding: 0 }} value="#material-list">
                    <AssociatedMaterial
                        labClient={labClient}
                        materialPriceLists={materialPriceLists}
                        materialPriceListForClient={materialPriceList}
                        savedPriceList={savedMaterialPriceList}
                        isUsingDefault={materialUseDefault}
                        onMaterialListChange={onMaterialListChange}
                        onUseDefault={onUseDefaultMaterial}
                    />
                </TabPanel>
            </TabContext>
        </Fragment>
    );
};

export default PriceList;
